import type { GroupStyleI, SimpleStyleT } from "tweenly"
import { renderStyles } from "tweenly"
import { DrawnResizeT, ScaleBox, scaleObjectsInBounds } from "./multiResize.helpers"
import { ObjectStylesWithId, ObjectsById } from "./wrapper.helpers"

/**
 * Live preview of a resize, the way Figma does it: the object follows the handle while it is
 * dragged instead of jumping to its new size on release.
 *
 * The preview is written straight to the DOM. Going through the store on every pointermove
 * would push one undo step per move and rebuild the object's tweens each time; the store is
 * written once, when the handle is released.
 */

const PREVIEW_PROPERTIES = ["left", "top", "width", "height"] as const
type PreviewPropertyT = (typeof PREVIEW_PROPERTIES)[number]

/**
 * Geometry to preview, in px — and, for a turned member of a multiselection that is being
 * sheared to fit (see affineMemberScale), its whole CSS transform. A missing property is
 * left as it is.
 */
export type PreviewStylesT = Partial<Record<PreviewPropertyT, number>> & { transform?: string }

export type ResizePreviewT = {
    apply: (element: HTMLElement, styles: PreviewStylesT) => void
    /** Put back the inline values every touched element had before the preview started. */
    restore: () => void
}

export const createResizePreview = (): ResizePreviewT => {
    const originals = new Map<HTMLElement, Record<PreviewPropertyT | "transform", string>>()

    const apply = (element: HTMLElement, styles: PreviewStylesT) => {
        if (!originals.has(element)) {
            originals.set(element, {
                left: element.style.left,
                top: element.style.top,
                width: element.style.width,
                height: element.style.height,
                transform: element.style.transform,
            })
        }
        if (styles.transform !== undefined) element.style.transform = styles.transform
        PREVIEW_PROPERTIES.forEach((property) => {
            const value = styles[property]
            if (value !== undefined && Number.isFinite(value)) {
                element.style[property] = value + "px"
            }
        })
    }

    const restore = () => {
        originals.forEach((original, element) => {
            PREVIEW_PROPERTIES.forEach((property) => {
                element.style[property] = original[property]
            })
            element.style.transform = original.transform
        })
        originals.clear()
    }

    return { apply, restore }
}

const numericStyle = (styles: SimpleStyleT[], property: PreviewPropertyT): number | undefined => {
    const value = Number(styles.find((style) => style.property === property)?.value)
    return Number.isFinite(value) ? value : undefined
}

/**
 * What a multiselection resize would commit, per object — the same `scaleObjectsInBounds`
 * that `resizeSelectedObjectsAction` runs on release, so the preview and the result cannot
 * drift apart.
 */
export const multiResizePreviewStyles = (
    objectsAbsolute: ObjectStylesWithId[],
    before: ScaleBox,
    after: ScaleBox,
    objectsById: ObjectsById,
    drawn?: DrawnResizeT
): { objectId: string; styles: PreviewStylesT }[] => {
    // an object can have two updates — its box and its transforms — previewed as one
    const byObject = new Map<string, PreviewStylesT>()
    scaleObjectsInBounds(objectsAbsolute, before, after, objectsById, drawn).forEach((update) => {
        const preview = byObject.get(update.object.id) ?? {}
        if (update.property === "transforms") {
            const rendered = renderStyles(
                { ...update.object, transforms: update.value as GroupStyleI[] },
                "inline"
            ) as { transform?: unknown }
            preview.transform = String(rendered.transform ?? "")
        } else {
            const styles = update.value as SimpleStyleT[]
            preview.left = numericStyle(styles, "left")
            preview.top = numericStyle(styles, "top")
            preview.width = numericStyle(styles, "width")
            preview.height = numericStyle(styles, "height")
        }
        byObject.set(update.object.id, preview)
    })
    return Array.from(byObject, ([objectId, styles]) => ({ objectId, styles }))
}
